/* ════════════════════════════════════════════════════════════
   API helpers — fetch, AES-GCM decrypt, activity logging
   Extracted from index.jsx — Tier 3 split

   Mutable session state lives on the exported `session` object so that
   it can be read/written across module boundaries without ES-module
   binding pitfalls. Replaced the original module-private `session.encSid`,
   `session.encKey`, `_ACT_USER` vars with `session.encSid`, `session.encKey`,
   `session.user`. `_SESSION_ID` is set once at module load.
   ──────────────────────────────────────────────────────────── */

export var API_BASE = window.location.origin.indexOf("localhost") >= 0 ? "http://localhost:5000" : "";

/* Mutable session state shared across modules */
export var session = {
  encSid: null,    /* session ID for encryption (was: session.encSid) */
  encKey: null,    /* CryptoKey (was: session.encKey) */
  user:   null     /* logged-in user object (was: _ACT_USER) */
};

/* ── Filter-value list serialization ──────────────────────────────────
   Dimension VALUES (molecule / manufacturer / product names) can themselves
   contain commas, e.g. "COMBINATION OF(LINAGLIPTIN,EMPAGLIFLOZIN)". The query
   string uses comma as the list separator, so a raw join(",") would make the
   server split one value into two and match zero rows. We escape any literal
   comma inside a value to U+001F (unit separator) before joining; the server
   reverses it after splitting. Keys without commas (dims/measures/periods) are
   left on the plain comma path. */
export function _encVals(arr) {
  return (arr || []).map(function(v) { return String(v).replace(/,/g, "\u001F"); }).join(",");
}

/* ── AES-256-GCM Decryption for encrypted data transport ── */
export function _b64ToBuf(b64) {
  var bin = atob(b64); var buf = new Uint8Array(bin.length);
  for (var i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf;
}

export function _importEncKey(keyB64) {
  /* r98: WebCrypto exists only in a SECURE context (https:// or localhost). On a plain
     http:// URL — e.g. a partner subdomain opened before its certificate / IIS https
     binding is in place — crypto.subtle is undefined and the login died with
     "Cannot read properties of undefined (reading 'importKey')". Say what to do. */
  if (!(window.crypto && window.crypto.subtle && window.crypto.subtle.importKey)) {
    var _loc = (typeof window !== "undefined" && window.location) ? window.location : { protocol: "", host: "" };
    var _msg = _loc.protocol === "http:" ? ("This portal must be opened over a secure connection. Use https://" + _loc.host + " (the address bar shows http://).")
             : "This browser does not provide secure cryptography (WebCrypto). Open the portal over https:// in a current Chrome, Edge, Firefox or Safari.";
    return Promise.reject(new Error(_msg));
  }
  var raw = _b64ToBuf(keyB64);
  return window.crypto.subtle.importKey("raw", raw, { name: "AES-GCM" }, false, ["decrypt"]);
}

/* Browser can gunzip server payloads via the native streams API. Gated so that
   if it's ever unavailable, we never advertise x-accept-gz and the server keeps
   sending the plain (uncompressed) encrypted path. */
var _GZ_OK = (typeof DecompressionStream !== "undefined");

/* ── Central dashboard filter-activity logger ──
   Any dashboard fetch that carries a user selection (manufacturer, product, class,
   region, year drill, ...) is logged as "dashboard_filter" with the selection, so the
   admin Activity Timeline shows WHICH filters each user applied. Noise controls:
   structural keys are dropped, empty selections are skipped, and identical events
   within 5s are deduped (re-renders / cache refreshes). */
var _lastFilterLog = { key: "", at: 0 };
var _DASHF_SKIP = { mode: 1, limit: 1, quick: 1 };
function _logDashFilter(dashKey, studyType, filters) {
  try {
    if (!filters) return;
    var f = filters;
    if (typeof f === "string") { try { f = JSON.parse(f); } catch (e) { return; } }
    if (typeof f !== "object") return;
    var clean = {}, has = false;
    for (var k in f) {
      if (_DASHF_SKIP[k]) continue;
      var v = f[k];
      if (v == null || v === "") continue;
      if (Array.isArray(v) && !v.length) continue;
      clean[k] = v; has = true;
    }
    if (!has) return;
    var key = dashKey + "|" + (studyType || "") + "|" + JSON.stringify(clean);
    var now = Date.now();
    if (_lastFilterLog.key === key && (now - _lastFilterLog.at) < 5000) return;
    _lastFilterLog = { key: key, at: now };
    logActivity("dashboard_filter", { studyType: studyType, dashboardName: dashKey, filters: clean });
  } catch (e) { /* never break a data fetch over logging */ }
}

/* In-flight dashboard requests, keyed by query string — lets a foreground open and a
   background prefetch of the SAME dashboard share one network call instead of duplicating
   heavy (10-50s) SQL queries. */
var _dashInflight = {};

export function _decryptPayload(encObj) {
  if (!session.encKey || !encObj || !encObj._enc) return Promise.resolve(encObj);
  var iv = _b64ToBuf(encObj.iv);
  var tag = _b64ToBuf(encObj.tag);
  var data = _b64ToBuf(encObj.d);
  /* GCM: ciphertext + tag concatenated */
  var combined = new Uint8Array(data.length + tag.length);
  combined.set(data); combined.set(tag, data.length);
  return window.crypto.subtle.decrypt({ name: "AES-GCM", iv: iv }, session.encKey, combined)
    .then(function(plain) {
      if (encObj.gz) {
        /* Decrypted bytes are gzipped JSON — inflate via the streams API. */
        var stream = new Blob([plain]).stream().pipeThrough(new DecompressionStream("gzip"));
        return new Response(stream).text().then(function(txt) { return JSON.parse(txt); });
      }
      return JSON.parse(new TextDecoder().decode(plain));
    });
}

export function _maybeDecrypt(data) {
  if (data && data._enc === 1) return _decryptPayload(data);
  return Promise.resolve(data);
}

/* ─── User Activity Logger (non-blocking, fire-and-forget) ─── */
export var _SESSION_ID = (function(){ try { var s = Math.random().toString(36).substring(2,10) + "-" + Date.now().toString(36); return s; } catch(e){ return ""; } })();

export function logActivity(activityType, extra) {
  if (!session.user) return;
  try {
    /* session.user shape from /api/auth/login response:
         { name, email, role, clientId, userId, clientName?, ... }
       Earlier this function read .username / .userName (don't exist) and only
       .clientName (server didn't always return it) — both came through as null,
       so UserActivityLog rows had User #ID and "—" for client. */
    var body = Object.assign({
      userId: session.user.userId || session.user.id,
      clientId: session.user.clientId,
      userName: session.user.name || session.user.fullName || session.user.email || "",
      clientName: session.user.clientName || "",
      sessionId: _SESSION_ID,
      activityType: activityType
    }, extra || {});
    /* Device attribution: send this browser's hardware fingerprint so every
       activity row (esp. logins) can be traced to the registered device in
       Device Authorization → Analysis. */
    try { if (!body.deviceId) body.deviceId = getOrCreateDeviceId(); } catch (eD) {}
    fetch(API_BASE + "/api/activity/log", {
      method: "POST",
      headers: {"Content-Type":"application/json"},
      body: JSON.stringify(body)
    }).catch(function(){ /* silent */ });
  } catch (e) { /* silent */ }
}


/* ════════════════════════════════════════════════════════════
   DEVICE-ID + TOKEN STORAGE (multi-layer)
   Used by api.login / api.logout — moved here in Tier 3 cleanup.
   ──────────────────────────────────────────────────────────── */
/* ── Device ID: persistent unique browser identifier ── */
var _deviceIdKey = "amsx_device_id";
/* Generate a hardware fingerprint stable across browsers on the same machine */
function _machineFingerprint() {
  var parts = [];
  try { parts.push(String(window.screen.width) + "x" + String(window.screen.height)); } catch(e) {}
  try { parts.push(String(window.screen.colorDepth || window.screen.pixelDepth || 24)); } catch(e) {}
  try { parts.push(navigator.platform || ""); } catch(e) {}
  try { parts.push(String(navigator.hardwareConcurrency || 0)); } catch(e) {}
  try { parts.push(Intl.DateTimeFormat().resolvedOptions().timeZone || ""); } catch(e) {}
  try { parts.push(String(navigator.deviceMemory || 0)); } catch(e) {}
  try { parts.push(String(navigator.maxTouchPoints || 0)); } catch(e) {}
  /* WebGL GPU fingerprint — unique per physical GPU even on identical machines */
  try {
    var canvas = document.createElement("canvas");
    var gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (gl) {
      var dbg = gl.getExtension("WEBGL_debug_renderer_info");
      if (dbg) {
        parts.push(gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) || "");
        parts.push(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || "");
      }
    }
  } catch(e) {}
  /* Simple hash → deterministic hex string */
  var raw = parts.join("|");
  var hash = 0;
  for (var i = 0; i < raw.length; i++) {
    var ch = raw.charCodeAt(i);
    hash = ((hash << 5) - hash) + ch;
    hash = hash & hash; /* 32-bit int */
  }
  var h1 = (hash >>> 0).toString(16).padStart(8, "0");
  /* Second hash pass for more entropy */
  var hash2 = 5381;
  for (var j = 0; j < raw.length; j++) {
    hash2 = ((hash2 << 5) + hash2) + raw.charCodeAt(j);
    hash2 = hash2 & hash2;
  }
  var h2 = (hash2 >>> 0).toString(16).padStart(8, "0");
  return "hw-" + h1 + "-" + h2 + "-" + raw.length.toString(16).padStart(4, "0");
}
export function getOrCreateDeviceId() {
  /* First: compute hardware fingerprint (same across all browsers on this machine) */
  var hwId = _machineFingerprint();
  /* Check if we already stored this fingerprint */
  try {
    var existing = localStorage.getItem(_deviceIdKey);
    /* If existing ID is a hardware fingerprint (starts with "hw-"), use it */
    if (existing && existing.indexOf("hw-") === 0) return existing;
    /* Migrate: old random UUID → replace with hardware fingerprint */
    localStorage.setItem(_deviceIdKey, hwId);
  } catch(e) {}
  return hwId;
}
export function getDeviceName() {
  var ua = navigator.userAgent || "";
  var browser = "Unknown";
  if (ua.indexOf("Chrome") > -1 && ua.indexOf("Edg") === -1) browser = "Chrome";
  else if (ua.indexOf("Firefox") > -1) browser = "Firefox";
  else if (ua.indexOf("Safari") > -1 && ua.indexOf("Chrome") === -1) browser = "Safari";
  else if (ua.indexOf("Edg") > -1) browser = "Edge";
  var os = "Unknown OS";
  if (ua.indexOf("Windows") > -1) os = "Windows";
  else if (ua.indexOf("Mac") > -1) os = "macOS";
  else if (ua.indexOf("Linux") > -1) os = "Linux";
  else if (ua.indexOf("Android") > -1) os = "Android";
  else if (ua.indexOf("iPhone") > -1 || ua.indexOf("iPad") > -1) os = "iOS";
  return browser + " on " + os;
}

/* ════════════════════════════════════════════════════════════════════
   DEVICE TOKEN — multi-layer storage
   The server issues a token on successful login. It's the primary device
   identifier (more reliable than the hardware fingerprint, which can
   collide on identical hardware). Stored in 4 places to resist partial
   browser-data clearing:
     1. localStorage    — survives session, cleared by "Clear browsing data"
     2. IndexedDB       — survives session, cleared by "Clear site data"
     3. sessionStorage  — survives reloads, cleared on tab close
     4. Cache Storage   — survives session, cleared by "Clear cached files"
   When all 4 are gone, the hardware fingerprint becomes a fallback.
════════════════════════════════════════════════════════════════════ */
var _tokenKey = "amsx_dev_tok";
var _tokenIDB = "amsx_auth";
var _tokenStore = "deviceToken";
var _tokenCache = "amsx-auth-v1";
var _tokenCacheReq = "/_amsx_token";

/* Open IndexedDB and ensure object store exists */
function _idbOpen() {
  return new Promise(function(resolve, reject) {
    try {
      var req = indexedDB.open(_tokenIDB, 1);
      req.onupgradeneeded = function(ev) {
        var db = ev.target.result;
        if (!db.objectStoreNames.contains(_tokenStore)) db.createObjectStore(_tokenStore);
      };
      req.onsuccess = function(ev) { resolve(ev.target.result); };
      req.onerror = function(ev) { reject(ev.target.error); };
    } catch(e) { reject(e); }
  });
}

function _idbPut(key, value) {
  return _idbOpen().then(function(db) {
    return new Promise(function(resolve, reject) {
      var tx = db.transaction(_tokenStore, "readwrite");
      tx.objectStore(_tokenStore).put(value, key);
      tx.oncomplete = function() { resolve(true); };
      tx.onerror = function(ev) { reject(ev.target.error); };
    });
  });
}

function _idbGet(key) {
  return _idbOpen().then(function(db) {
    return new Promise(function(resolve, reject) {
      var tx = db.transaction(_tokenStore, "readonly");
      var req = tx.objectStore(_tokenStore).get(key);
      req.onsuccess = function() { resolve(req.result); };
      req.onerror = function(ev) { reject(ev.target.error); };
    });
  });
}

function _idbDel(key) {
  return _idbOpen().then(function(db) {
    return new Promise(function(resolve) {
      var tx = db.transaction(_tokenStore, "readwrite");
      tx.objectStore(_tokenStore).delete(key);
      tx.oncomplete = function() { resolve(true); };
      tx.onerror = function() { resolve(false); }; /* swallow errors */
    });
  });
}

/* Store token in all 4 layers in parallel — best-effort, no failure stops others */
export function storeDeviceToken(token) {
  if (!token) return;
  /* Layer 1: localStorage */
  try { localStorage.setItem(_tokenKey, token); } catch(e) {}
  /* Layer 2: sessionStorage */
  try { sessionStorage.setItem(_tokenKey, token); } catch(e) {}
  /* Layer 3: IndexedDB */
  try { _idbPut(_tokenStore, token).catch(function() {}); } catch(e) {}
  /* Layer 4: Cache Storage */
  try {
    if (window.caches && caches.open) {
      caches.open(_tokenCache).then(function(c) {
        c.put(_tokenCacheReq, new Response(token, {headers: {"Content-Type": "text/plain"}}));
      }).catch(function() {});
    }
  } catch(e) {}
}

/* Retrieve token from any available layer (first hit wins).
   Returns a Promise<string|null>. */
export function retrieveDeviceToken() {
  /* Synchronous layers checked first */
  try {
    var t1 = localStorage.getItem(_tokenKey);
    if (t1) return Promise.resolve(t1);
  } catch(e) {}
  try {
    var t2 = sessionStorage.getItem(_tokenKey);
    if (t2) return Promise.resolve(t2);
  } catch(e) {}
  /* Async layers — try IDB first, fall back to Cache Storage */
  return _idbGet(_tokenStore).then(function(t3) {
    if (t3) return t3;
    if (window.caches && caches.open) {
      return caches.open(_tokenCache).then(function(c) {
        return c.match(_tokenCacheReq).then(function(resp) {
          return resp ? resp.text() : null;
        });
      }).catch(function() { return null; });
    }
    return null;
  }).catch(function() { return null; });
}

/* Clear token from all layers — used on explicit logout or revoke */
export function clearDeviceToken() {
  try { localStorage.removeItem(_tokenKey); } catch(e) {}
  try { sessionStorage.removeItem(_tokenKey); } catch(e) {}
  try { _idbDel(_tokenStore).catch(function() {}); } catch(e) {}
  try {
    if (window.caches && caches.open) {
      caches.open(_tokenCache).then(function(c) { c.delete(_tokenCacheReq); }).catch(function() {});
    }
  } catch(e) {}
}

export var api = {
  /* r87: SAMPLE mode — while on, every /api/reports/* request carries
     x-amsx-sample: 1 so a LOCKED report may generate for a sample deck */
  _sampleMode: false,
  setSampleMode: function(on) { api._sampleMode = !!on; },
  /* r146: Watermark switch on the report page (only users with cleanExport may turn it off) */
  _cleanExport: false,
  setCleanExport: function(on) { api._cleanExport = !!on; },
  _fetch: function(path, opts) {
    var headers = {"Content-Type":"application/json"};
    if (session.encSid) headers["x-enc-sid"] = session.encSid;
    if (api._sampleMode && String(path).indexOf("/api/reports/") === 0) headers["x-amsx-sample"] = "1";
    if (_GZ_OK) headers["x-accept-gz"] = "1";
    return fetch(API_BASE + path, Object.assign({ headers: headers }, opts || {}))
    .then(function(r) {
      /* 401 from a data endpoint means our session was rejected or expired by
         the server-side gate (Layer 1 security). Wipe the local session so a
         stale encSid doesn't keep getting reused, and dispatch a custom event
         that App can listen to and trigger a redirect to login.
         (Don't fire on /api/login or /api/devices/poll — those don't require
         a session and a 401 there means actual creds wrong.
         Don't fire on /api/activity/log either — telemetry endpoint, a 401
         there is benign and should never log the user out.) */
      if (r.status === 401 && path.indexOf("/api/login") < 0
                            && path.indexOf("/api/devices/poll/") < 0
                            && path.indexOf("/api/activity/log") < 0) {
        try {
          session.encSid = null;
          session.encKey = null;
          session.user = null;
        } catch(e) {}
        try { window.dispatchEvent(new CustomEvent("amsx:session-expired")); } catch(e) {}
        throw new Error("Session expired — please log in again");
      }
      if (!r.ok) {
        /* Error bodies pass through the SAME response-encryption wrapper as data
           (Layer 1 wraps res.json wholesale), so e.error sat inside {_enc:...}
           and every admin failure surfaced as a bare "API error <status>".
           Decrypt before extracting the message; fall back to the status text
           only when the body is unreadable (e.g. a proxy HTML page). */
        return r.json()
          .then(function(e){ return Promise.resolve(_maybeDecrypt(e)).catch(function(){ return e; }); })
          .catch(function(){ return {}; })
          .then(function(e){ throw new Error((e && e.error) || "API error " + r.status); });
      }
      /* An /api path answered by the SPA catch-all (an older server.js without
         this endpoint) returns index.html with 200 OK — say so clearly instead
         of surfacing a JSON parse error (Noura 2026-09-28). */
      var ct = "";
      try { ct = String(r.headers.get("content-type") || ""); } catch (eCt) {}
      if (ct.indexOf("text/html") >= 0) {
        throw new Error("This report needs a server update \u2014 the running server.js does not include this endpoint yet. Replace server.js with the latest version and restart pm2.");
      }
      return r.json();
    })
    .then(_maybeDecrypt);
  },
  /* _fetchRaw — returns full response JSON even on 403 (for device errors) */
  _fetchRaw: function(path, opts) {
    return fetch(API_BASE + path, Object.assign({
      headers: {"Content-Type":"application/json"}
    }, opts || {}))
    .then(function(r) {
      return r.json().then(function(data) {
        data._httpStatus = r.status;
        return data;
      });
    });
  },
  /* Auth */
  login: function(email, password) {
    var deviceId = getOrCreateDeviceId();
    var deviceName = getDeviceName();
    /* Retrieve previously-issued token (if any) from the multi-layer storage.
       Sent alongside the hardware fingerprint so the server can do token-first
       auth and fall back to fingerprint when the token is missing. */
    return retrieveDeviceToken().then(function(existingTok) {
      return api._fetchRaw("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({
          email: email,
          password: password,
          deviceId: deviceId,
          deviceName: deviceName,
          deviceToken: existingTok || null
        })
      });
    }).then(function(data) {
      if (data._httpStatus === 403 && (data.error === "device_pending" || data.error === "device_revoked")) {
        var err = new Error(data.message);
        err.deviceError = data.error;
        err.deviceId = data.deviceId;
        err.deviceRecordId = data.deviceRecordId;
        throw err;
      }
      if (data._httpStatus >= 400) {
        var _lgErr = new Error(data.error || data.message || "Login failed");
        if (data.code) _lgErr.code = data.code; /* e.g. PORTAL_LAUNCH */
        throw _lgErr;
      }
      /* Store encryption session key BEFORE returning so the very next request
         from the caller can decrypt the response. The old fire-and-forget
         `.then()` returned `data` while encKey was still null, which used to
         be harmless (server only encrypted when sid+key were present) but
         became a race after Layer 1+: server now encrypts on every authorized
         response from the moment the session row exists, including the
         immediately-following request that fires before the import resolved.
         Return the data only after the key import resolves. */
      if (data._encSid && data._encKey) {
        session.encSid = data._encSid;
        return _importEncKey(data._encKey).then(function(k) {
          session.encKey = k;
          /* Persist server-issued device token to all 4 storage layers. */
          if (data.deviceToken) { try { storeDeviceToken(data.deviceToken); } catch(e) {} }
          return data;
        });
      }
      /* No encryption session for some reason — proceed without */
      /* Persist server-issued device token to all 4 storage layers.
         If the server didn't issue one (e.g. legacy login path), this is a no-op. */
      if (data.deviceToken) {
        try { storeDeviceToken(data.deviceToken); } catch(e) {}
      }
      return data;
    });
  },
  /* Logout — destroys the server-side session row. Always resolves (even on
     network failure) so the UI can always clear local state. The server
     endpoint itself is open (no requireSession) so it's safe to call with
     an already-expired sid. */
  logout: function() {
    var sid = session.encSid;
    if (!sid) return Promise.resolve({ success: true });
    return fetch(API_BASE + "/api/auth/logout", {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-enc-sid": sid },
      body: JSON.stringify({ _sid: sid })
    }).then(function(){ return { success: true }; })
      .catch(function(){ return { success: true }; })
      .then(function(r){
        try { session.encSid = null; session.encKey = null; session.user = null; } catch(e) {}
        return r;
      });
  },
  /* Self-service password change. Server verifies the current password,
     stores the new bcrypt hash, clears the first-login flag and revokes the
     user's OTHER sessions (this one stays alive). Rejections (wrong current
     password, too short, same as current) surface as thrown Errors with the
     server's message — the modal shows them inline. */
  changePassword: function(currentPassword, newPassword) {
    return api._fetch("/api/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ currentPassword: currentPassword, newPassword: newPassword })
    });
  },
  /* "Keep current password" on the first-login prompt — just clears the
     MustChangePassword flag so the prompt doesn't reappear. */
  keepPassword: function() {
    return api._fetch("/api/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ keepCurrent: true })
    });
  },
  /* Device polling */
  pollDevice: function(deviceId) {
    return api._fetch("/api/devices/poll/" + encodeURIComponent(deviceId));
  },
  /* Device management (admin) */
  getDevices: function() { return api._fetch("/api/devices"); },
  getDeviceNotifications: function() { return api._fetch("/api/devices/notifications"); },
  getDeviceLiveStats: function() { return api._fetch("/api/devices/live-stats"); },
  getDeviceAnalysis: function(id) { return api._fetch("/api/admin/devices/" + id + "/analysis"); },
  /* Admin Team (delegated admin accounts) — full admins only */
  getAdminTeam: function() { return api._fetch("/api/admin/team"); },
  createTeamMember: function(m) { return api._fetch("/api/admin/team", {method:"POST", body:JSON.stringify(m)}); },
  promoteUserToAdmin: function(m) { return api._fetch("/api/admin/team/promote", {method:"POST", body:JSON.stringify(m)}); },
  updateTeamMember: function(id, m) { return api._fetch("/api/admin/team/" + id, {method:"PUT", body:JSON.stringify(m)}); },
  deleteTeamMember: function(id) { return api._fetch("/api/admin/team/" + id, {method:"DELETE"}); },
  /* Master data export (full admins only) */
  getMasterStructure: function(userId, studyType, geo, dims, layout, per) { var _q = "?userId=" + userId + "&studyType=" + encodeURIComponent(studyType) + "&geo=" + geo + "&layout=" + (layout || "wide") + (dims && dims.length ? "&dims=" + encodeURIComponent(dims.join(",")) : ""); if (per) { if (per.ptype) _q += "&ptype=" + per.ptype; if (per.sels && per.sels.length) _q += "&sels=" + encodeURIComponent(per.sels.join(",")); ["fromY","fromS","toY","toS"].forEach(function(k){ if (per[k]) _q += "&" + k + "=" + per[k]; }); } return api._fetch("/api/admin/master-export/structure" + _q); },
  generateMasterExport: function(m) { return api._fetch("/api/admin/master-export/generate", {method:"POST", body:JSON.stringify(m)}); },
  getMasterExportStatus: function(jobId) { return api._fetch("/api/admin/master-export/status/" + jobId); },
  approveDevice: function(id, approvedBy) {
    return api._fetch("/api/devices/" + id + "/approve", {method:"PUT", body:JSON.stringify({approvedBy:approvedBy})});
  },
  revokeDevice: function(id) { return api._fetch("/api/devices/" + id + "/revoke", {method:"PUT"}); },
  deleteDevice: function(id) { return api._fetch("/api/devices/" + id, {method:"DELETE"}); },
  dismissDeviceNotification: function(id) {
    return api._fetch("/api/devices/notifications/" + id + "/dismiss", {method:"PUT"});
  },
  /* Activity Analytics (admin)
     Each accepts an optional `scope = {clientId, userId}` to restrict the
     analysis to a specific client or user. Omitted/null = global view. */
  _scopeQs: function(days, scope) {
    var qs = "?days=" + (days == null ? 30 : days);
    if (scope && scope.clientId) qs += "&clientId=" + encodeURIComponent(scope.clientId);
    if (scope && scope.userId)   qs += "&userId="   + encodeURIComponent(scope.userId);
    return qs;
  },
  getActivityOverview: function(days)  { return api._fetch("/api/admin/activity/overview"  + api._scopeQs(days)); },
  getActivityUsers:    function(days)  { return api._fetch("/api/admin/activity/users"     + api._scopeQs(days)); },
  getActivityClients:  function(days)  { return api._fetch("/api/admin/activity/clients"   + api._scopeQs(days)); },
  getActivityDashboards: function(days){ return api._fetch("/api/admin/activity/dashboards" + api._scopeQs(days)); },
  getActivityQueries:  function(days, scope) { return api._fetch("/api/admin/activity/queries"  + api._scopeQs(days, scope)); },
  getActivityFilters:  function(days, scope) { return api._fetch("/api/admin/activity/filters"  + api._scopeQs(days, scope)); },
  getActivityDimCombos:function(days, scope) { return api._fetch("/api/admin/activity/dim-combos" + api._scopeQs(days, scope)); },
  getActivityUser: function(userId, days) { return api._fetch("/api/admin/activity/user/" + userId + "?days=" + (days == null ? 30 : days)); },
  getActivityRecommendations: function(days) { return api._fetch("/api/admin/activity/recommendations?days=" + (days == null ? 30 : days)); },
  /* Report quota (Noura 2026-09-24): a deck payload with quotaExceeded means the
     client's distinct-subject limit for that report is used up — broadcast it so
     ReportsPanel can show WHY the deck stayed empty. */
  _rqNotify: function(d) {
    if (d && d.quotaExceeded) { try { window.dispatchEvent(new CustomEvent("amsx-report-quota", { detail: d })); } catch (e) {} }
    return d;
  },
  getReportCardsMeta: function(studyType) {
    return api._fetch("/api/reports/cards-meta?studyType=" + encodeURIComponent(studyType || ""))
      .then(function(d){ return (d && d.available) ? d : null; }).catch(function(){ return null; });
  },
  /* r87: order the full report (sample mode) + admin request queue */
  orderFullReport: function(p) { return api._fetch("/api/reports/order", { method: "POST", body: JSON.stringify(p || {}) }); },
  getReportOrders: function(status, days) { return api._fetch("/api/admin/report-orders?status=" + encodeURIComponent(status || "new") + "&days=" + (days || 90)); },
  setReportOrderStatus: function(id, status) { return api._fetch("/api/admin/report-orders/" + id, { method: "PUT", body: JSON.stringify({ status: status }) }); },
  /* r95: conversation + e-mail answers of a request; mailer status */
  getReportOrderMessages: function(id) { return api._fetch("/api/admin/report-orders/" + id + "/messages"); },
  replyReportOrder: function(id, p) { return api._fetch("/api/admin/report-orders/" + id + "/reply", { method: "POST", body: JSON.stringify(p || {}) }); },
  getMailStatus: function() { return api._fetch("/api/admin/mail-status"); },
  /* r97: partner layer (Scenario A) */
  getBranding: function() { var _h9 = ""; try { _h9 = "?host=" + encodeURIComponent(window.location.hostname || ""); } catch (e) {} return fetch(API_BASE + "/api/branding" + _h9).then(function(r){ return r.json(); }).catch(function(){ return { partner: null }; }); },
  getPartners: function() { return api._fetch("/api/admin/partners"); },
  createPartner: function(p) { return api._fetch("/api/admin/partners", { method: "POST", body: JSON.stringify(p || {}) }); },
  updatePartner: function(id, p) { return api._fetch("/api/admin/partners/" + id, { method: "PUT", body: JSON.stringify(p || {}) }); },
  uploadPartnerLogo: function(id, file) { var fd = new FormData(); fd.append("logo", file); var h = {}; if (session.encSid) h["x-enc-sid"] = session.encSid; return fetch(API_BASE + "/api/admin/partners/" + id + "/logo", { method: "POST", headers: h, body: fd }).then(function(r){ return r.json().then(function(j){ if (!r.ok) throw new Error(j.error || "Upload failed"); return j; }); }); },
  getPartnerUsage: function(partnerId, from, to) { return api._fetch("/api/admin/partner-usage?partnerId=" + encodeURIComponent(partnerId || "") + "&from=" + encodeURIComponent(from || "") + "&to=" + encodeURIComponent(to || "")); },
  partnerUsageCsvUrl: function(partnerId, from, to) { return API_BASE + "/api/admin/partner-usage?format=csv&partnerId=" + encodeURIComponent(partnerId || "") + "&from=" + encodeURIComponent(from || "") + "&to=" + encodeURIComponent(to || ""); },
  sendPartnerUsage: function(partnerId, month) { return api._fetch("/api/admin/partner-usage/send", { method: "POST", body: JSON.stringify({ partnerId: partnerId, month: month }) }); },
  getReportQuota: function(studyType) {
    return api._fetch("/api/reports/quota?studyType=" + encodeURIComponent(studyType || ""))
      .then(function(d){ return d || { enabled: false }; }).catch(function(){ return { enabled: false }; });
  },
  getMarketOverviewReport: function(studyType, period) { return api._fetch("/api/reports/market-overview?studyType=" + encodeURIComponent(studyType||"iraq") + "&period=" + encodeURIComponent(period||"year")).then(api._rqNotify); },
  getMoleculeList: function(studyType) { return api._fetch("/api/reports/molecule-list?studyType=" + encodeURIComponent(studyType||"iraq")); },
  getProductList: function(studyType) { return api._fetch("/api/reports/product-list?studyType=" + encodeURIComponent(studyType||"iraq")); },   /* r182: Product Overview picker */
  getMoleculeContext: function(studyType, molecule) {
    var _mcMk = ""; try { if (typeof window !== "undefined" && window.__amsxDashMarket) _mcMk = "&market=" + window.__amsxDashMarket; } catch (eDMk3) {}
    return api._fetch("/api/reports/molecule-context?studyType=" + encodeURIComponent(studyType||"iraq") + "&molecule=" + encodeURIComponent(molecule||"") + _mcMk);
  },
  /* POST: the focus set can be 170+ manufacturer names — far past IIS's query-string limit */
  getMoleculeOverviewReport: function(studyType, sc) { return api._fetch("/api/reports/molecule-overview?studyType=" + encodeURIComponent(studyType||"iraq"), { method: "POST", body: JSON.stringify({ molecule: sc.molecule || "", atc3: sc.atc3 || [], manufacturers: sc.allMfrs ? [] : (sc.manufacturers || []), periodType: sc.periodType || "year" }) }).then(api._rqNotify); },
  getAtc2List: function(studyType, level) { return api._fetch("/api/reports/atc2-list?studyType=" + encodeURIComponent(studyType||"iraq") + (level ? "&level=" + encodeURIComponent(level) : "")); },
  getTherapeuticOverviewReport: function(studyType, sc) { return api._fetch("/api/reports/therapeutic-overview?studyType=" + encodeURIComponent(studyType||"iraq"), { method: "POST", body: JSON.stringify({ atc2: sc.atc2 || "", periodType: sc.periodType || "year", level: sc.level || "atc2" }) }).then(api._rqNotify); },
  /* Iraq TOTAL market deck (private + KIMADIA) — the new Iraq Market Overview */
  getIraqTotalMarketReport: function(studyType, period) {
    return api._fetch("/api/reports/iraq-total-market?studyType=" + encodeURIComponent(studyType || "iraq")
      + (period ? "&period=" + encodeURIComponent(period) : "")).then(api._rqNotify);
  },
  /* KIMADIA whole-book tender deck (Market Overview -> Sector: Public, Iraq) */
  getKimadiaMarketReport: function(studyType, focusClass, focusMolecule, period) {
    return api._fetch("/api/reports/kimadia-market?studyType=" + encodeURIComponent(studyType || "iraq")
      + (period ? "&period=" + encodeURIComponent(period) : "")   /* r116: year | mat | quarter */
      + (focusClass ? "&focusClass=" + encodeURIComponent(focusClass) : "")
      + (focusMolecule ? "&focusMolecule=" + encodeURIComponent(focusMolecule) : "")).then(api._rqNotify);
  },
  getKimadiaTenderReport: function(studyType, sc) { return api._fetch("/api/reports/kimadia-tender?studyType=" + encodeURIComponent(studyType||"iraq"), { method: "POST", body: JSON.stringify({ scope: sc.scope || "atc2", value: sc.value || "", periodType: sc.periodType || "year" }) }); },
  getTherapeuticMedicalReport: function(studyType, sc) { return api._fetch("/api/reports/therapeutic-medical?studyType=" + encodeURIComponent(studyType||"jordan"), { method: "POST", body: JSON.stringify({ atc2: sc.atc2 || "", level: sc.level || "atc2", periodType: sc.periodType || "year" }) }); },   /* r114: + periodType */
  getTherapeuticTenderReport: function(studyType, sc) { return api._fetch("/api/reports/therapeutic-tender?studyType=" + encodeURIComponent(studyType||"jordan"), { method: "POST", body: JSON.stringify({ atc2: sc.atc2 || "", level: sc.level || "atc2", periodType: sc.periodType || "year" }) }); },
  getAgentList: function(studyType) { return api._fetch("/api/reports/agent-list?studyType=" + encodeURIComponent(studyType||"iraq")); },
  getAgentOverviewReport: function(studyType, sc) { return api._fetch("/api/reports/agent-overview?studyType=" + encodeURIComponent(studyType||"iraq"), { method: "POST", body: JSON.stringify({ agent: sc.agent || "", periodType: sc.periodType || "year" }) }).then(api._rqNotify); },
  getManufacturerList: function(studyType) { return api._fetch("/api/reports/manufacturer-list?studyType=" + encodeURIComponent(studyType||"iraq")); },
  getManufacturerOverviewReport: function(studyType, manufacturer) { return api._fetch("/api/reports/manufacturer-overview?studyType=" + encodeURIComponent(studyType||"iraq") + "&manufacturer=" + encodeURIComponent(manufacturer||"")).then(api._rqNotify); },
  /* Clients */
  getClients: function() { return api._fetch("/api/clients"); },
  getReportsCatalog: function() { return api._fetch("/api/reports/catalog"); },
  saveClient: function(c) {
    return (c.id && !isNaN(c.id)) ? api._fetch("/api/clients/" + c.id, {method:"PUT", body:JSON.stringify(c)})
                                  : api._fetch("/api/clients", {method:"POST", body:JSON.stringify(c)});
  },
  toggleClient: function(id) { return api._fetch("/api/clients/" + id + "/toggle", {method:"PUT"}); },
  deleteClient: function(id) { return api._fetch("/api/clients/" + id, {method:"DELETE"}); },
  /* Users */
  getUsers: function() { return api._fetch("/api/users"); },
  saveUser: function(u) {
    return (u.id && !isNaN(u.id)) ? api._fetch("/api/users/" + u.id, {method:"PUT", body:JSON.stringify(u)})
                                  : api._fetch("/api/users", {method:"POST", body:JSON.stringify(u)});
  },
  toggleUser: function(id) { return api._fetch("/api/users/" + id + "/toggle", {method:"PUT"}); },
  deleteUser: function(id) { return api._fetch("/api/users/" + id, {method:"DELETE"}); },
  incrementExtract: function(id) { return api._fetch("/api/users/" + id + "/extract", {method:"PUT"}); },
  /* ── Export provenance (Layer 1) ──
     Register an export with the server BEFORE the file is generated. The server
     records identity from the SESSION (not this payload) plus the device
     fingerprint, and returns { exportId } for embedding in the file. */
  registerExport: function(payload) {
    var p = Object.assign({}, payload || {});
    try { p.deviceId = getOrCreateDeviceId(); } catch (e) {}
    return api._fetch("/api/exports/register", { method: "POST", body: JSON.stringify(p) });
  },
  /* Admin: audit log of traceability actions */
  getAdminAudit: function(days) { return api._fetch("/api/admin/audit?days=" + (days == null ? 30 : days)); },
  getActivityAdoption: function() { return api._fetch("/api/admin/activity/adoption"); },
  /* ── Layer 2 data fingerprint (admin) — USER-level ── */
  getFingerprintUsers: function() { return api._fetch("/api/admin/fingerprint/users"); },
  setUserFingerprint: function(id, enabled) {
    return api._fetch("/api/admin/users/" + id + "/fingerprint", { method: "PUT", body: JSON.stringify({ enabled: !!enabled }) });
  },
  bulkFingerprint: function(enabled, userIds) {
    return api._fetch("/api/admin/fingerprint/bulk", { method: "POST", body: JSON.stringify({ enabled: !!enabled, userIds: userIds || [] }) });
  },
  getFingerprintClients: function() { return api._fetch("/api/admin/fingerprint/clients"); },
  setClientFingerprint: function(id, enabled) {
    return api._fetch("/api/admin/clients/" + id + "/fingerprint", { method: "PUT", body: JSON.stringify({ enabled: !!enabled }) });
  },
  getUserMarkedSkus: function(userId, studyType) {
    return api._fetch("/api/admin/fingerprint/marked?userId=" + encodeURIComponent(userId) + "&studyType=" + encodeURIComponent(studyType || ""));
  },
  getFingerprintCandidates: function(studyType) {
    return api._fetch("/api/admin/fingerprint/candidates?studyType=" + encodeURIComponent(studyType || ""));
  },
  checkFingerprint: function(studyType, rows) {
    return api._fetch("/api/admin/fingerprint/check", { method: "POST", body: JSON.stringify({ studyType: studyType, rows: rows }) });
  },
  /* Admin: provenance lookup — pass {exportId} or {clientId,userId,days,limit} */
  getExportLog: function(params) {
    var qs = [];
    var _p = params || {};
    ["exportId","seq","clientId","userId","days","limit"].forEach(function(k){
      if (_p[k] != null && _p[k] !== "") qs.push(k + "=" + encodeURIComponent(_p[k]));
    });
    return api._fetch("/api/admin/exports" + (qs.length ? "?" + qs.join("&") : ""));
  },
  /* Pharma Data */
  getPharmaData: function(clientId, studyType) {
    var params = [];
    if (clientId) params.push("clientId=" + clientId);
    if (studyType) params.push("studyType=" + studyType);
    var qs = params.length ? "?" + params.join("&") : "";
    var t0 = performance.now();
    var hdrs = {"Content-Type":"application/json"};
    if (session.encSid) hdrs["x-enc-sid"] = session.encSid;
    if (_GZ_OK) hdrs["x-accept-gz"] = "1";
    return fetch(API_BASE + "/api/pharma-data" + qs, {
      headers: hdrs
    }).then(function(r) {
      var t1 = performance.now();
      console.log("  [perf] network: " + Math.round(t1 - t0) + "ms");
      if (!r.ok) return r.json()
        .then(function(e){ return Promise.resolve(_maybeDecrypt(e)).catch(function(){ return e; }); })
        .catch(function(){ return {}; })
        .then(function(e){ throw new Error((e && e.error) || "API error " + r.status); });
      return r.json().then(_maybeDecrypt).then(function(data) {
        var t2 = performance.now();
        console.log("  [perf] JSON parse: " + Math.round(t2 - t1) + "ms, rows: " + (data.rowCount || (data.rows && data.rows.length) || 0));
        console.log("  [perf] TOTAL: " + Math.round(t2 - t0) + "ms");
        return data;
      });
    });
  },
  /* Fast dashboard endpoint — server pre-aggregates by dashboard dimensions */
  getDashboardData: function(clientId, studyType, dashKey, quick, filters, channel, market) {
    var params = [];
    if (clientId) params.push("clientId=" + clientId);
    if (studyType) params.push("studyType=" + studyType);
    if (dashKey) params.push("dashKey=" + dashKey);
    if (quick) params.push("quick=1");
    /* Custom market — the server scopes the WHOLE payload (every chart) to it */
    if (market) { params.push("market=" + encodeURIComponent(market)); _logDashFilter(dashKey || "dashboard", studyType, { market: market }); }
    /* Import Channel (Libya Direct/Parallel) — global server-side scope */
    if (channel) { params.push("channel=" + encodeURIComponent(channel)); _logDashFilter(dashKey || "dashboard", studyType, { importChannel: channel }); }
    if (filters) {
      var _fs = typeof filters === "string" ? filters : JSON.stringify(filters);
      if (_fs && _fs !== "{}") { params.push("filters=" + encodeURIComponent(_fs)); _logDashFilter(dashKey || "dashboard", studyType, filters); }
    }
    var qs = params.length ? "?" + params.join("&") : "";
    /* In-flight dedupe: share one request if the same dashboard is already loading
       (e.g. a background prefetch racing the user's foreground click). */
    if (_dashInflight[qs]) return _dashInflight[qs];
    var hdrs = {"Content-Type":"application/json"};
    if (session.encSid) hdrs["x-enc-sid"] = session.encSid;
    if (_GZ_OK) hdrs["x-accept-gz"] = "1";
    var _p = fetch(API_BASE + "/api/pharma-data/dashboard" + qs, {
      headers: hdrs
    }).then(function(r) {
      if (!r.ok) return r.json()
        .then(function(e){ return Promise.resolve(_maybeDecrypt(e)).catch(function(){ return e; }); })
        .catch(function(){ return {}; })
        .then(function(e){ throw new Error((e && e.error) || "API error " + r.status); });
      return r.json();
    }).then(_maybeDecrypt);
    _dashInflight[qs] = _p;
    _p.then(function(){ delete _dashInflight[qs]; }, function(){ delete _dashInflight[qs]; });
    return _p;
  },
  /* Governorate drill-down: fetch on-demand when region clicked */
  /* r47: product × molecule competition for the Leading Products detail */
  getProductMolecules: function(studyType, product, measure, curWin, prevWin, topN, channel, level) {
    var qs = "studyType=" + encodeURIComponent(studyType || "iraq") + "&product=" + encodeURIComponent(product || "") +
             "&measure=" + encodeURIComponent(measure || "valUSD") + "&cur=" + encodeURIComponent(JSON.stringify(curWin || [])) +
             "&prev=" + encodeURIComponent(JSON.stringify(prevWin || [])) + "&topN=" + (topN || 5) +
             (channel ? "&channel=" + encodeURIComponent(channel) : "") + (level ? "&level=" + encodeURIComponent(level) : "");
    return fetch(API_BASE + "/api/pharma-data/product-molecules?" + qs, {
      headers: Object.assign({"Content-Type":"application/json"}, session.encSid ? {"x-enc-sid": session.encSid} : {})
    }).then(function(r) { return r.json(); }).then(_maybeDecrypt);
  },
  /* r54: Generic/Originator + Local/Imported split at PRODUCT level (Leading Products) */
  getProductTypeSplit: function(studyType, measure, curWin, prevWin, topN, channel, level) {
    var qs = "studyType=" + encodeURIComponent(studyType || "iraq") + "&measure=" + encodeURIComponent(measure || "valUSD") +
             "&cur=" + encodeURIComponent(JSON.stringify(curWin || [])) + "&prev=" + encodeURIComponent(JSON.stringify(prevWin || [])) + "&topN=" + (topN || 5) +
             (channel ? "&channel=" + encodeURIComponent(channel) : "") + (level ? "&level=" + encodeURIComponent(level) : "");
    return fetch(API_BASE + "/api/pharma-data/product-type-split?" + qs, {
      headers: Object.assign({"Content-Type":"application/json"}, session.encSid ? {"x-enc-sid": session.encSid} : {})
    }).then(function(r) { return r.json(); }).then(_maybeDecrypt);
  },
  getManufacturerPortfolio: function(studyType, manufacturer, measure, curWin, prevWin, topN, channel) {
    var qs = "studyType=" + encodeURIComponent(studyType || "iraq") + "&manufacturer=" + encodeURIComponent(manufacturer || "") +
             "&measure=" + encodeURIComponent(measure || "valUSD") + "&cur=" + encodeURIComponent(JSON.stringify(curWin || [])) +
             "&prev=" + encodeURIComponent(JSON.stringify(prevWin || [])) + "&topN=" + (topN || 10) +
             (channel ? "&channel=" + encodeURIComponent(channel) : "");
    return fetch(API_BASE + "/api/pharma-data/manufacturer-portfolio?" + qs, {
      headers: Object.assign({"Content-Type":"application/json"}, session.encSid ? {"x-enc-sid": session.encSid} : {})
    }).then(function(r) { return r.json(); }).then(_maybeDecrypt);
  },
  getManufacturerRegions: function(studyType, manufacturer, measure, curWin, topN, channel) {
    var qs = "studyType=" + encodeURIComponent(studyType || "iraq") + "&manufacturer=" + encodeURIComponent(manufacturer || "") +
             "&measure=" + encodeURIComponent(measure || "valUSD") + "&cur=" + encodeURIComponent(JSON.stringify(curWin || [])) + "&topN=" + (topN || 5) +
             (channel ? "&channel=" + encodeURIComponent(channel) : "");
    return fetch(API_BASE + "/api/pharma-data/manufacturer-regions?" + qs, {
      headers: Object.assign({"Content-Type":"application/json"}, session.encSid ? {"x-enc-sid": session.encSid} : {})
    }).then(function(r) { return r.json(); }).then(_maybeDecrypt);
  },
  getDashboardGov: function(studyType, region) {
    _logDashFilter("governorate_drilldown", studyType, { region: region });
    var hdrs = {"Content-Type":"application/json"};
    if (session.encSid) hdrs["x-enc-sid"] = session.encSid;
    if (_GZ_OK) hdrs["x-accept-gz"] = "1";
    var _gvMk = ""; try { if (typeof window !== "undefined" && window.__amsxDashMarket) _gvMk = "&market=" + window.__amsxDashMarket; } catch (eDMk2) {}
    return fetch(API_BASE + "/api/pharma-data/dashboard-gov?studyType=" + (studyType||"iraq") + "&region=" + encodeURIComponent(region) + _gvMk, {
      headers: hdrs
    }).then(function(r) { return r.json(); }).then(_maybeDecrypt);
  },
  /* Dashboard V2: pre-computed chart data — ~100 rows instead of 50K */
  getDashboardV2: function(studyType, dashKey, opts) {
    if (opts && (opts.region || opts.year || opts.quarter)) _logDashFilter(dashKey || "market_overview", studyType, { region: opts.region, year: opts.year, quarter: opts.quarter, measure: opts.measure });
    var params = ["studyType=" + (studyType||"iraq"), "dashKey=" + (dashKey||"market_overview")];
    if (opts) {
      if (opts.measure) params.push("measure=" + opts.measure);
      if (opts.region) params.push("region=" + encodeURIComponent(opts.region));
      if (opts.year) params.push("year=" + opts.year);
      if (opts.quarter) params.push("quarter=" + opts.quarter);
      if (opts.topN) params.push("topN=" + opts.topN);
    }
    return fetch(API_BASE + "/api/pharma-data/dashboard-v2?" + params.join("&"), {
      headers: Object.assign({"Content-Type":"application/json"}, session.encSid ? {"x-enc-sid": session.encSid} : {})
    }).then(function(r) {
      if (!r.ok) return r.json().then(function(e){ throw new Error(e.error || "V2 error " + r.status); });
      return r.json();
    }).then(_maybeDecrypt);
  },
  /* ── Custom region split (per-user) ── */
  getUserRegionMap: function(studyType) {
    return fetch(API_BASE + "/api/user/region-map?studyType=" + encodeURIComponent(studyType || "iraq"), {
      headers: Object.assign({"Content-Type":"application/json"}, session.encSid ? {"x-enc-sid": session.encSid} : {})
    }).then(function(r) { return r.ok ? r.json().then(_maybeDecrypt) : { enabled: false, regions: {} }; })
      .catch(function() { return { enabled: false, regions: {} }; });
  },
  saveUserRegionMap: function(studyType, enabled, regions) {
    return fetch(API_BASE + "/api/user/region-map", {
      method: "POST",
      headers: Object.assign({"Content-Type":"application/json"}, session.encSid ? {"x-enc-sid": session.encSid} : {}),
      body: JSON.stringify({ studyType: studyType || "iraq", enabled: !!enabled, regions: regions || {} })
    }).then(function(r) {
      if (!r.ok) return r.json().then(_maybeDecrypt).then(function(e){ throw new Error(e.error || "Save failed (" + r.status + ")"); });
      return r.json().then(_maybeDecrypt);
    });
  },
  /* Market Overview origin donut drill: manufacturers importing from one country */
  getOriginMfrs: function(studyType, coo, clientId) {
    var params = ["studyType=" + encodeURIComponent(studyType || "iraq"), "coo=" + encodeURIComponent(coo || "")];
    if (clientId) params.push("clientId=" + encodeURIComponent(clientId));
    var _h = {"Content-Type":"application/json"};
    if (session.encSid) _h["x-enc-sid"] = session.encSid;
    if (_GZ_OK) _h["x-accept-gz"] = "1";
    return fetch(API_BASE + "/api/pharma-data/origin-mfrs?" + params.join("&"), { headers: _h })
      .then(function(r) { return r.ok ? r.json().then(_maybeDecrypt) : { rows: [] }; })
      .catch(function() { return { rows: [] }; });
  },
  /* New Launches dashboard — market entries inside the last N quarters.
     opts: { win:4|8|12, level, topN, mode:"main"|"skus", entity, clientId } */
  getNewLaunches: function(studyType, opts) {
    var params = ["studyType=" + encodeURIComponent(studyType || "iraq")];
    try { if (typeof window !== "undefined" && window.__amsxDashMarket) params.push("market=" + window.__amsxDashMarket); } catch (eDMk1) {}
    if (opts) { for (var k in opts) { if (opts[k] != null && opts[k] !== "") params.push(k + "=" + encodeURIComponent(opts[k])); } }
    if (opts && opts.mode !== "skus") _logDashFilter("new_launches", studyType, opts);
    var fb = (opts && opts.mode === "skus") ? { skus: [], molecule: "", molTotal: {} }
                                            : { rows: [], counts: {}, byQuarter: [], series: [], parents: [], contribs: [], meta: {} };
    var _h = {"Content-Type":"application/json"};
    if (session.encSid) _h["x-enc-sid"] = session.encSid;
    if (_GZ_OK) _h["x-accept-gz"] = "1";
    return fetch(API_BASE + "/api/pharma-data/new-launches?" + params.join("&"), { headers: _h })
      .then(function(r) { return r.ok ? r.json().then(_maybeDecrypt) : fb; }).catch(function() { return fb; });
  },
  /* Live search a single dimension column on server */
  searchDim: function(studyType, dim, query, limit, filters) {
    var params = ["studyType=" + (studyType||"iraq"), "dim=" + encodeURIComponent(dim)];
    try { if (typeof window !== "undefined" && window.__amsxPivotMarket) params.push("market=" + window.__amsxPivotMarket); } catch (eMk4) {}
    if (query) params.push("q=" + encodeURIComponent(query));
    if (limit) params.push("limit=" + limit);
    if (filters) {
      Object.keys(filters).forEach(function(k) {
        if (k === "atcFilters" || k === "nfcFilters") {
          var nested = filters[k] || {};
          Object.keys(nested).forEach(function(nk) {
            if (nested[nk] && nested[nk].length) params.push(nk.toLowerCase() + "=" + encodeURIComponent(_encVals(nested[nk])));
          });
        } else {
          var v = filters[k];
          if (v && Array.isArray(v) && v.length) params.push(k.toLowerCase() + "=" + encodeURIComponent(_encVals(v)));
        }
      });
    }
    return fetch(API_BASE + "/api/pharma-data/search-dim?" + params.join("&"), {
      headers: Object.assign({"Content-Type":"application/json"}, session.encSid ? {"x-enc-sid": session.encSid} : {})
    }).then(function(r) { return r.ok ? r.json().then(_maybeDecrypt) : []; }).catch(function() { return []; });
  },
  /* Competitive Analysis (medical) — lightweight, targeted endpoint.
     mode "top" -> array; mode "class"/"competitors" -> object. Returns null-safe default on failure. */
  /* Regional Distribution (Cross Analysis): Sales (units/USD) vs Rx (prescriptions/
     patients/doctors) by Manufacturer / ATC3 / Product, scoped to a region.
     mode=regions -> { regions:[...] }; else -> { dim, label, region, rows:[{nm,units,usd,rx,pt,dr}] }. */
  getRegionalDistribution: function(studyType, opts) {
    if (opts && opts.mode !== "regions") _logDashFilter("regional_distribution", studyType, opts);
    var params = ["studyType=" + encodeURIComponent(studyType||"")];
    if (opts) { for (var k in opts) { if (opts[k] != null && opts[k] !== "") params.push(k + "=" + encodeURIComponent(opts[k])); } }
    var fb = (opts && opts.mode === "regions") ? { regions:[] } : (opts && opts.mode === "region_summary") ? { rows:[] } : { dim:(opts&&opts.dim)||"manufacturer", label:"", region:"", rows:[] };
    var _h = {"Content-Type":"application/json"};
    if (session.encSid) _h["x-enc-sid"] = session.encSid;
    if (_GZ_OK) _h["x-accept-gz"] = "1";
    return fetch(API_BASE + "/api/regional-distribution?" + params.join("&"), { headers: _h })
      .then(function(r) { return r.ok ? r.json().then(_maybeDecrypt) : fb; }).catch(function() { return fb; });
  },
  getCrossCompetition: function(studyType, opts) {
    if (opts && (opts.cls || opts.region)) _logDashFilter("cross_competition", studyType, opts);
    var params = ["studyType=" + encodeURIComponent(studyType||"")];
    if (opts) { for (var k in opts) { if (opts[k] != null && opts[k] !== "") params.push(k + "=" + encodeURIComponent(opts[k])); } }
    var fb = (opts && opts.mode === "heatmap") ? { cells:[], regionTotals:[] } : { rows:[], cls:"" };
    var _h = {"Content-Type":"application/json"};
    if (session.encSid) _h["x-enc-sid"] = session.encSid;
    if (_GZ_OK) _h["x-accept-gz"] = "1";
    return fetch(API_BASE + "/api/cross-competition?" + params.join("&"), { headers: _h })
      .then(function(r) { return r.ok ? r.json().then(_maybeDecrypt) : fb; }).catch(function() { return fb; });
  },
  getManufacturerCross: function(studyType, opts) {
    _logDashFilter("manufacturer_cross", studyType, opts);
    var params = ["studyType=" + encodeURIComponent(studyType||"")];
    if (opts) { for (var k in opts) { if (opts[k] != null && opts[k] !== "") params.push(k + "=" + encodeURIComponent(opts[k])); } }
    var fb = { rows: [] };
    var _h = {"Content-Type":"application/json"};
    if (session.encSid) _h["x-enc-sid"] = session.encSid;
    if (_GZ_OK) _h["x-accept-gz"] = "1";
    return fetch(API_BASE + "/api/manufacturer-cross?" + params.join("&"), { headers: _h })
      .then(function(r) { return r.ok ? r.json().then(_maybeDecrypt) : fb; }).catch(function() { return fb; });
  },
  /* Custom Markets — client-defined competitor universes for the competition and
     Price & SKU dashboards. list → {markets:[{id,name,definition,clientWide,own}]}. */
  getMarkets: function(studyType, clientId) {
    var qs = "?studyType=" + encodeURIComponent(studyType || "") + (clientId ? "&clientId=" + encodeURIComponent(clientId) : "");
    return api._fetch("/api/markets" + qs).then(function(d){ return d && d.markets ? d : { markets: [] }; }).catch(function(){ return { markets: [] }; });
  },
  saveMarket: function(payload) { return api._fetch("/api/markets", { method: "POST", body: JSON.stringify(payload || {}) }); },
  /* Global Definition Market wizard: anchor company's molecules / ATC3 / competitors */
  getMarketPortfolio: function(studyType, manufacturer) {
    var qs = "?studyType=" + encodeURIComponent(studyType || "") + "&manufacturer=" + encodeURIComponent(manufacturer || "");
    return api._fetch("/api/markets/portfolio" + qs).then(function(d){ return d || { molecules: [], atc3: [], competitors: [] }; })
      .catch(function(){ return { molecules: [], atc3: [], competitors: [] }; });
  },
  /* rev 21: ALL manufacturers active on EXTRA (outside-portfolio) ATC3 classes /
     molecules — powers the wizard's "add beyond the anchor's portfolio" flow */
  getMarketCompFor: function(studyType, atc3, molecules) {
    return api._fetch("/api/markets/compfor", { method: "POST",
      body: JSON.stringify({ studyType: studyType || "", atc3: atc3 || [], molecules: molecules || [] }) })
      .then(function(d){ return d || { competitors: [], compTypes: {}, compPairs: [], pairs: [], compMol: [] }; })
      .catch(function(){ return { competitors: [], compTypes: {}, compPairs: [], pairs: [], compMol: [] }; });
  },
  deleteMarket: function(id, clientId) { return api._fetch("/api/markets/delete", { method: "POST", body: JSON.stringify({ id: id, clientId: clientId }) }); },
  /* Share a market group — sharedScope: null | "team" | "users" (+ ids), SavedQueries pattern */
  shareMarket: function(id, sharedScope, sharedUserIds, clientId) {
    return api._fetch("/api/markets/share", { method: "POST",
      body: JSON.stringify({ id: id, sharedScope: sharedScope || null, sharedUserIds: sharedUserIds || [], clientId: clientId }) });
  },
  /* Cross Insights (second-generation cross dashboards): conversion | regionmap |
     substitution | subtrend | funnel | launches | price | names. Null-safe fallback
     per mode so a failed call renders an empty state, never a crash. */
  getCrossInsights: function(studyType, opts) {
    if (opts && opts.mode && opts.mode !== "names") _logDashFilter("cross_insights_" + opts.mode, studyType, opts);
    var params = ["studyType=" + encodeURIComponent(studyType||"")];
    if (opts) { for (var k in opts) { if (opts[k] != null && opts[k] !== "") params.push(k + "=" + encodeURIComponent(opts[k])); } }
    var m = (opts && opts.mode) || "";
    var fb = m === "funnel" ? { specs:[], cross:[], regions:[] }
      : m === "launches" ? { launches:[], sales:[], med:[] }
      : m === "price" ? { nm:"", cls:"", sales:[], med:[], compMed:[], compSales:[] }
      : m === "subtrend" ? { mol:"", sales:[], med:[] }
      : m === "conversion" ? { dim:(opts&&opts.dim)||"product", label:"", region:"", rows:[], totals:{} }
      : { rows:[] };
    var _h = {"Content-Type":"application/json"};
    if (session.encSid) _h["x-enc-sid"] = session.encSid;
    if (_GZ_OK) _h["x-accept-gz"] = "1";
    return fetch(API_BASE + "/api/cross-insights?" + params.join("&"), { headers: _h })
      .then(function(r) { return r.ok ? r.json().then(_maybeDecrypt) : fb; }).catch(function() { return fb; });
  },
  getMedicalCompetitive: function(studyType, mode, opts) {
    if (mode !== "names" && mode !== "dimvals") _logDashFilter("competitive_analysis", studyType, opts);
    var params = ["studyType=" + encodeURIComponent(studyType||""), "mode=" + encodeURIComponent(mode||"top")];
    if (opts) { for (var k in opts) { if (opts[k] != null && opts[k] !== "") params.push(k + "=" + encodeURIComponent(opts[k])); } }
    var fb = (mode === "top" || mode === "names" || mode === "dimvals") ? [] : (mode === "competitors" ? {products:[],totals:[]} : (mode === "bundle" ? {rows:[],meta:[]} : {atc3:null,atc4:null,molecule:null}));
    var _h = {"Content-Type":"application/json"};
    if (session.encSid) _h["x-enc-sid"] = session.encSid;
    if (_GZ_OK) _h["x-accept-gz"] = "1";   /* gzip-before-encrypt for large payloads (e.g. the bundle), same as pharma */
    return fetch(API_BASE + "/api/medical-competitive?" + params.join("&"), {
      headers: _h
    }).then(function(r) { return r.ok ? r.json().then(_maybeDecrypt) : fb; }).catch(function() { return fb; });
  },
  getMedicalWindow: function(studyType, opts) {
    var params = ["studyType=" + encodeURIComponent(studyType||"")];
    if (opts) { for (var k in opts) { if (opts[k] != null && opts[k] !== "") params.push(k + "=" + encodeURIComponent(opts[k])); } }
    var fb = { counts:{ cur:{}, prev:{}, yoy:{} }, dims:{} };
    var _h = {"Content-Type":"application/json"};
    if (session.encSid) _h["x-enc-sid"] = session.encSid;
    if (_GZ_OK) _h["x-accept-gz"] = "1";
    return fetch(API_BASE + "/api/medical-window?" + params.join("&"), { headers: _h })
      .then(function(r) { return r.ok ? r.json().then(_maybeDecrypt) : fb; }).catch(function() { return fb; });
  },
  getMedicalDynamics: function(opts) {
    var params = [];
    if (opts) { for (var k in opts) { if (opts[k] != null && opts[k] !== "") params.push(k + "=" + encodeURIComponent(opts[k])); } }
    var fb = (opts && opts.mode === "products") ? { products:[] } : (opts && opts.mode === "months") ? { months:[] } : { flow:null, trend:[] };
    var _h = {"Content-Type":"application/json"};
    if (session.encSid) _h["x-enc-sid"] = session.encSid;
    if (_GZ_OK) _h["x-accept-gz"] = "1";
    return fetch(API_BASE + "/api/medical-dynamics?" + params.join("&"), { headers: _h })
      .then(function(r) { return r.ok ? r.json().then(_maybeDecrypt) : fb; }).catch(function() { return fb; });
  },
  /* Fetch aggregated data — server does GROUP BY, returns ~200 rows instead of 500K */
  getFilteredData: function(clientId, filters, dims, studyType, level, measures, periodMode, periodKeys, topNOpts) {
    var params = [];
    if (clientId) params.push("clientId=" + clientId);
    if (studyType) params.push("studyType=" + studyType);
    /* Global Definition Market on the pivot — set by MarketHeaderControl when
       the selected group has "Also apply in the Market tab" enabled */
    try { if (typeof window !== "undefined" && window.__amsxPivotMarket) params.push("market=" + window.__amsxPivotMarket); } catch (eMk) {}
    if (dims && dims.length) params.push("dims=" + encodeURIComponent(dims.join(",")));
    if (level != null) params.push("level=" + level);
    if (measures && measures.length) params.push("measures=" + encodeURIComponent(measures.join(",")));
    if (periodMode) params.push("periodMode=" + encodeURIComponent(periodMode));
    if (periodKeys && periodKeys.length) params.push("periodKeys=" + encodeURIComponent(periodKeys.join(",")));
    if (topNOpts && topNOpts.n) {
      params.push("topN=" + topNOpts.n);
      if (topNOpts.measure) params.push("topNMeasure=" + encodeURIComponent(topNOpts.measure));
      if (topNOpts.dir) params.push("topNDir=" + encodeURIComponent(topNOpts.dir));
      if (topNOpts.showOthers) params.push("topNShowOthers=true");
      if (topNOpts.alwaysDim && topNOpts.always && topNOpts.always.length) {
        params.push("topNAlwaysDim=" + encodeURIComponent(topNOpts.alwaysDim));
        params.push("topNAlways=" + encodeURIComponent(topNOpts.always.join("||")));
      }
    }
    if (filters.flow && filters.flow.length)               params.push("flow=" + encodeURIComponent(_encVals(filters.flow)));
    if (filters.source && filters.source.length)           params.push("source=" + encodeURIComponent(_encVals(filters.source)));
    if (filters.docSalesRegion && filters.docSalesRegion.length) params.push("docsalesregion=" + encodeURIComponent(_encVals(filters.docSalesRegion)));
    if (filters.phSalesRegion && filters.phSalesRegion.length)   params.push("phsalesregion=" + encodeURIComponent(_encVals(filters.phSalesRegion)));
    if (filters.country && filters.country.length)         params.push("country=" + encodeURIComponent(_encVals(filters.country)));
    if (filters.sector && filters.sector.length)           params.push("sector=" + encodeURIComponent(_encVals(filters.sector)));
    if (filters.manufacturer && filters.manufacturer.length) params.push("manufacturer=" + encodeURIComponent(_encVals(filters.manufacturer)));
    if (filters.medicine && filters.medicine.length)       params.push("medicine=" + encodeURIComponent(_encVals(filters.medicine)));
    if (filters.sku && filters.sku.length)                 params.push("sku=" + encodeURIComponent(_encVals(filters.sku)));
    if (filters.agent && filters.agent.length)             params.push("agent=" + encodeURIComponent(_encVals(filters.agent)));
    if (filters.orderNo && filters.orderNo.length)         params.push("orderno=" + encodeURIComponent(_encVals(filters.orderNo)));
    if (filters.LaunchDate && filters.LaunchDate.length)   params.push("launchdate=" + encodeURIComponent(_encVals(filters.LaunchDate)));
    if (filters.tenderStatus && filters.tenderStatus.length) params.push("tenderstatus=" + encodeURIComponent(_encVals(filters.tenderStatus)));
    if (filters.region && filters.region.length)           params.push("region=" + encodeURIComponent(_encVals(filters.region)));
    if (filters.governorate && filters.governorate.length) params.push("governorate=" + encodeURIComponent(_encVals(filters.governorate)));
    if (filters.continent && filters.continent.length) params.push("continent=" + encodeURIComponent(_encVals(filters.continent)));
    if (filters.subContinent && filters.subContinent.length) params.push("subcontinent=" + encodeURIComponent(_encVals(filters.subContinent)));
    if (filters.countryOfOrigin && filters.countryOfOrigin.length) params.push("countryoforigin=" + encodeURIComponent(_encVals(filters.countryOfOrigin)));
    if (filters.manufacturerType && filters.manufacturerType.length) params.push("manufacturertype=" + encodeURIComponent(_encVals(filters.manufacturerType)));
    if (filters.localImported && filters.localImported.length) params.push("local_imported=" + encodeURIComponent(_encVals(filters.localImported)));
    if (filters.subManufacturer && filters.subManufacturer.length) params.push("submanufacturer=" + encodeURIComponent(_encVals(filters.subManufacturer)));
    if (filters.subProduct && filters.subProduct.length) params.push("subproduct=" + encodeURIComponent(_encVals(filters.subProduct)));
    if (filters.productSource && filters.productSource.length) params.push("productsource=" + encodeURIComponent(_encVals(filters.productSource)));
    /* Medical / doctor / pharmacy / patient dimensions */
    if (filters.form && filters.form.length) params.push("form=" + encodeURIComponent(_encVals(filters.form)));
    if (filters.pack && filters.pack.length) params.push("pack=" + encodeURIComponent(_encVals(filters.pack)));
    if (filters.strength && filters.strength.length) params.push("strength=" + encodeURIComponent(_encVals(filters.strength)));
    if (filters.aloneCombined && filters.aloneCombined.length) params.push("alonecombined=" + encodeURIComponent(_encVals(filters.aloneCombined)));
    if (filters.docRegion && filters.docRegion.length) params.push("docregion=" + encodeURIComponent(_encVals(filters.docRegion)));
    if (filters.docSpec && filters.docSpec.length) params.push("docspec=" + encodeURIComponent(_encVals(filters.docSpec)));
    if (filters.docCity && filters.docCity.length) params.push("doccity=" + encodeURIComponent(_encVals(filters.docCity)));
    if (filters.phRegion && filters.phRegion.length) params.push("phregion=" + encodeURIComponent(_encVals(filters.phRegion)));
    if (filters.phCity && filters.phCity.length) params.push("phcity=" + encodeURIComponent(_encVals(filters.phCity)));
    if (filters.patientAge && filters.patientAge.length) params.push("patientage=" + encodeURIComponent(_encVals(filters.patientAge)));
    if (filters.patientGender && filters.patientGender.length) params.push("patientgender=" + encodeURIComponent(_encVals(filters.patientGender)));
    if (filters.diagnosis && filters.diagnosis.length) params.push("diagnosis=" + encodeURIComponent(_encVals(filters.diagnosis)));
    var atc = filters.atcFilters || {};
    if (atc.ATC1 && atc.ATC1.length) params.push("atc1=" + encodeURIComponent(_encVals(atc.ATC1)));
    if (atc.ATC2 && atc.ATC2.length) params.push("atc2=" + encodeURIComponent(_encVals(atc.ATC2)));
    if (atc.ATC3 && atc.ATC3.length) params.push("atc3=" + encodeURIComponent(_encVals(atc.ATC3)));
    if (atc.ATC4 && atc.ATC4.length) params.push("atc4=" + encodeURIComponent(_encVals(atc.ATC4)));
    if (atc.MOLECULE && atc.MOLECULE.length) params.push("molecule=" + encodeURIComponent(_encVals(atc.MOLECULE)));
    var nfc = filters.nfcFilters || {};
    if (nfc.NFC && nfc.NFC.length) params.push("nfc=" + encodeURIComponent(_encVals(nfc.NFC)));
    if (nfc.NFC2 && nfc.NFC2.length) params.push("nfc2=" + encodeURIComponent(_encVals(nfc.NFC2)));
    if (nfc.NFC3 && nfc.NFC3.length) params.push("nfc3=" + encodeURIComponent(_encVals(nfc.NFC3)));
    var qs = params.length ? "?" + params.join("&") : "";
    var fullUrl = API_BASE + "/api/pharma-data/aggregate" + qs;
    var t0 = performance.now();
    /* If URL is too long (IIS default limit ~2048 chars), use POST with body instead */
    var useBody = fullUrl.length > 1800;
    var fetchOpts;
    var fetchUrl;
    if (useBody) {
      fetchUrl = API_BASE + "/api/pharma-data/aggregate";
      /* Parse params back into a body object */
      var bodyObj = {};
      params.forEach(function(p){ var eq = p.indexOf("="); if (eq > 0) { bodyObj[p.substring(0,eq)] = decodeURIComponent(p.substring(eq+1)); } });
      fetchOpts = {
        method: "POST",
        headers: Object.assign({"Content-Type":"application/json"}, session.encSid ? {"x-enc-sid": session.encSid} : {}),
        body: JSON.stringify(bodyObj)
      };
    } else {
      fetchUrl = fullUrl;
      fetchOpts = { headers: Object.assign({"Content-Type":"application/json"}, session.encSid ? {"x-enc-sid": session.encSid} : {}) };
    }
    return fetch(fetchUrl, fetchOpts).then(function(r) {
      if (!r.ok) return r.json()
        .then(function(e){ return Promise.resolve(_maybeDecrypt(e)).catch(function(){ return e; }); })
        .catch(function(){ return {}; })
        .then(function(e){ throw new Error((e && e.error) || "API error " + r.status); });
      return r.json().then(_maybeDecrypt).then(function(data) {
        console.log("  [perf] aggregate: " + Math.round(performance.now() - t0) + "ms, rows: " + (data.rowCount || 0) + (useBody ? " (POST)" : ""));
        return data;
      });
    });
  },
  getDimensions: function(clientId, studyType) {
    var params = [];
    if (clientId) params.push("clientId=" + clientId);
    if (studyType) params.push("studyType=" + studyType);
    try { if (typeof window !== "undefined" && window.__amsxPivotMarket) params.push("market=" + window.__amsxPivotMarket); } catch (eMk1) {}
    var qs = params.length ? "?" + params.join("&") : "";
    return api._fetch("/api/pharma-data/dimensions" + qs);
  },
  getCascadedDims: function(studyType, filters) {
    var params = ["studyType=" + (studyType || "iraq")];
    try { if (typeof window !== "undefined" && window.__amsxPivotMarket) params.push("market=" + window.__amsxPivotMarket); } catch (eMk2) {}
    if (filters) {
      Object.keys(filters).forEach(function(k) {
        if (k === "atcFilters" || k === "nfcFilters") {
          var nested = filters[k] || {};
          Object.keys(nested).forEach(function(nk) {
            if (nested[nk] && nested[nk].length) params.push(nk.toLowerCase() + "=" + encodeURIComponent(_encVals(nested[nk])));
          });
        } else {
          var v = filters[k];
          if (v && Array.isArray(v) && v.length) params.push(k.toLowerCase() + "=" + encodeURIComponent(_encVals(v)));
        }
      });
    }
    return api._fetch("/api/pharma-data/cascaded-dims?" + params.join("&"));
  },
  getDimValues: function(studyType, filters) {
    /* Optional cascade context: same filter serialization as getCascadedDims,
       so the dropdown VALUE BARS are aggregated within the active selection. */
    var params = ["studyType=" + (studyType || "iraq")];
    try { if (typeof window !== "undefined" && window.__amsxPivotMarket) params.push("market=" + window.__amsxPivotMarket); } catch (eMk3) {}
    if (filters) {
      Object.keys(filters).forEach(function(k) {
        if (k === "atcFilters" || k === "nfcFilters") {
          var nested = filters[k] || {};
          Object.keys(nested).forEach(function(nk) {
            if (nested[nk] && nested[nk].length) params.push(nk.toLowerCase() + "=" + encodeURIComponent(_encVals(nested[nk])));
          });
        } else {
          var v = filters[k];
          if (v && Array.isArray(v) && v.length) params.push(k.toLowerCase() + "=" + encodeURIComponent(_encVals(v)));
        }
      });
    }
    return api._fetch("/api/pharma-data/dim-values?" + params.join("&"));
  },
  voiceQuery: function(params) {
    return fetch(API_BASE + "/api/pharma-data/voice-query", {
      method: "POST",
      headers: Object.assign({"Content-Type":"application/json"}, session.encSid ? {"x-enc-sid": session.encSid} : {}),
      body: JSON.stringify(params)
    }).then(function(r) { return r.json(); }).then(_maybeDecrypt);
  },
  aiQuery: function(query, schema, sampleValues) {
    return fetch(API_BASE + "/api/ai-query", {
      method: "POST",
      headers: Object.assign({"Content-Type":"application/json"}, session.encSid ? {"x-enc-sid": session.encSid} : {}),
      body: JSON.stringify({ query: query, schema: schema, sampleValues: sampleValues })
    }).then(function(r) { return r.json(); }).then(_maybeDecrypt);
  },
  uploadPharmaData: function(file, mapping) {
    var fd = new FormData();
    fd.append("file", file);
    if (mapping) fd.append("mapping", JSON.stringify(mapping));
    return fetch(API_BASE + "/api/pharma-data/upload", {method:"POST", body:fd})
      .then(function(r){ return r.json(); });
  },
  /* Co-Prescription Intelligence (medical studies) — product-anchored */
  getCoRxProducts: function(studyType, clientId, q, type) {
    var qs = "?studyType=" + encodeURIComponent(studyType || "jordan_medical") + (clientId ? "&clientId=" + clientId : "") + (q ? "&q=" + encodeURIComponent(q) : "") + (type ? "&type=" + encodeURIComponent(type) : "");
    return api._fetch("/api/pharma-data/co-prescription/products" + qs);
  },
  getCoPrescription: function(studyType, clientId, opts) {
    var o = opts || {};
    var params = ["studyType=" + encodeURIComponent(studyType || "jordan_medical"), "product=" + encodeURIComponent(o.product || "")];
    if (o.anchorType) params.push("anchorType=" + encodeURIComponent(o.anchorType));
    if (clientId) params.push("clientId=" + clientId);
    if (o.year) params.push("year=" + o.year);
    if (o.quarter) params.push("quarter=" + o.quarter);
    if (o.minSupport) params.push("minSupport=" + o.minSupport);
    if (o.speciality) params.push("speciality=" + encodeURIComponent(o.speciality));
    if (o.diagnosis) params.push("diagnosis=" + encodeURIComponent(o.diagnosis));
    if (o.docRegion) params.push("docRegion=" + encodeURIComponent(o.docRegion));
    return api._fetch("/api/pharma-data/co-prescription?" + params.join("&"));
  },
  /* Saved Queries */
  getSavedQueries: function(userId, clientId, studyType) {
    var qs = "?userId=" + (userId||"") + "&clientId=" + (clientId||"");
    if (studyType) qs += "&studyType=" + encodeURIComponent(studyType);
    return api._fetch("/api/saved-queries" + qs);
  },
  saveQuery: function(q) { return api._fetch("/api/saved-queries", {method:"POST", body:JSON.stringify(q)}); },
  /* Update an existing saved query's name + selection (preserves sharing config) */
  updateQuery: function(extId, q) {
    return api._fetch("/api/saved-queries/" + extId, {method:"PUT", body:JSON.stringify(q)});
  },
  deleteQuery: function(extId) { return api._fetch("/api/saved-queries/" + extId, {method:"DELETE"}); },
  /* Legacy toggle (kept for back-compat with older UI). Toggles team/private. */
  shareQuery: function(extId) { return api._fetch("/api/saved-queries/" + extId + "/share", {method:"PUT"}); },
  /* New granular share config — sharedScope: null | "team" | "users", sharedUserIds: [int,int,...] */
  shareQueryConfig: function(extId, sharedScope, sharedUserIds) {
    return api._fetch("/api/saved-queries/" + extId + "/share-config", {
      method: "PUT",
      body: JSON.stringify({ sharedScope: sharedScope || null, sharedUserIds: sharedUserIds || [] })
    });
  },
  /* Study Types */
  getStudyTypes: function() { return api._fetch("/api/study-types"); },
  getStudyFeatures: function() { return api._fetch("/api/admin/study-features"); },
  addStudyFeature: function(f) { return api._fetch("/api/admin/study-features", {method:"POST", body:JSON.stringify(f)}); },
  updateStudyFeature: function(id, f) { return api._fetch("/api/admin/study-features/" + id, {method:"PUT", body:JSON.stringify(f)}); },
  deleteStudyFeature: function(id) { return api._fetch("/api/admin/study-features/" + id, {method:"DELETE"}); },
  toggleStudyFeature: function(id) { return api._fetch("/api/admin/study-features/" + id + "/toggle", {method:"PUT"}); },
  setStudyGranularity: function(id, value) { return api._fetch("/api/admin/study-types/" + id + "/period-granularity", {method:"PUT", body:JSON.stringify({value: value || ""})}); },   /* r113 */
  copyFeatures: function(fromId, toId) { return api._fetch("/api/admin/copy-features", {method:"POST", body:JSON.stringify({fromStudyTypeId:fromId, toStudyTypeId:toId})}); },
  getStudyDashboards: function() { return api._fetch("/api/admin/study-dashboards"); },
  addStudyDashboard: function(d) { return api._fetch("/api/admin/study-dashboards", {method:"POST", body:JSON.stringify(d)}); },
  updateStudyDashboard: function(id, d) { return api._fetch("/api/admin/study-dashboards/" + id, {method:"PUT", body:JSON.stringify(d)}); },
  deleteStudyDashboard: function(id) { return api._fetch("/api/admin/study-dashboards/" + id, {method:"DELETE"}); },
  getStudyTypeConfig: function(code) { return api._fetch("/api/study-type-config/" + code); },
  getStudyData: function(studyType, clientId) {
    var qs = "?studyType=" + studyType + (clientId ? "&clientId=" + clientId : "");
    return api._fetch("/api/study-data" + qs);
  },
  getStudyDataAggregate: function(studyType, clientId, filters, dims, level, measures, periodMode, periodKeys, topNOpts) {
    var params = ["studyType=" + studyType];
    if (clientId) params.push("clientId=" + clientId);
    if (dims && dims.length) params.push("dims=" + dims.join(","));
    if (level != null) params.push("level=" + level);
    if (measures && measures.length) params.push("measures=" + encodeURIComponent(measures.join(",")));
    if (periodMode) params.push("periodMode=" + encodeURIComponent(periodMode));
    if (periodKeys && periodKeys.length) params.push("periodKeys=" + encodeURIComponent(periodKeys.join(",")));
    if (topNOpts && topNOpts.n) {
      params.push("topN=" + topNOpts.n);
      if (topNOpts.measure) params.push("topNMeasure=" + encodeURIComponent(topNOpts.measure));
      if (topNOpts.dir) params.push("topNDir=" + encodeURIComponent(topNOpts.dir));
      if (topNOpts.showOthers) params.push("topNShowOthers=true");
      if (topNOpts.alwaysDim && topNOpts.always && topNOpts.always.length) {
        params.push("topNAlwaysDim=" + encodeURIComponent(topNOpts.alwaysDim));
        params.push("topNAlways=" + encodeURIComponent(topNOpts.always.join("||")));
      }
    }
    if (filters) {
      Object.keys(filters).forEach(function(k) {
        if (k === "atcFilters") {
          /* Flatten ATC filters: atcFilters.ATC1 → atc1=..., atcFilters.MOLECULE → molecule=... */
          var atc = filters[k] || {};
          Object.keys(atc).forEach(function(ak) {
            if (atc[ak] && atc[ak].length) {
              params.push(ak.toLowerCase() + "=" + encodeURIComponent(_encVals(atc[ak])));
            }
          });
        } else {
          var v = filters[k];
          if (v && Array.isArray(v) && v.length) params.push(k.toLowerCase() + "=" + encodeURIComponent(_encVals(v)));
        }
      });
    }
    return api._fetch("/api/study-data/aggregate?" + params.join("&"));
  },
  getStudyDimensions: function(studyType, clientId) {
    var qs = "?studyType=" + studyType + (clientId ? "&clientId=" + clientId : "");
    return api._fetch("/api/study-data/dimensions" + qs);
  },
};
